/**
 * Admin Account Managers Page
 * 
 * CRUD management for Account Managers + Unassigned Clients section.
 */

import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, UserCog, Users } from "lucide-react";
import { toast } from "sonner";

interface AccountManager {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  title: string | null;
  is_active: boolean;
  max_clients: number;
  specialty: string[] | null;
  active_client_count?: number;
}

interface UnassignedAgent {
  id: string;
  name: string;
  company: string | null;
  vertical: string;
  email: string | null;
}

const SPECIALTIES = [
  "life_insurance",
  "mortgage",
];

const formatSpecialty = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminAccountManagers() {
  const { user } = useAuth();
  const [managers, setManagers] = useState<AccountManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAM, setEditingAM] = useState<AccountManager | null>(null);

  // Unassigned clients
  const [unassignedAgents, setUnassignedAgents] = useState<UnassignedAgent[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningAgentId, setAssigningAgentId] = useState<string | null>(null);
  const [assigningAgentName, setAssigningAgentName] = useState("");
  const [assignSelectedAmId, setAssignSelectedAmId] = useState("");

  // Form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formTitle, setFormTitle] = useState("Account Manager");
  const [formMaxClients, setFormMaxClients] = useState(50);
  const [formSpecialty, setFormSpecialty] = useState<string[]>([]);

  useEffect(() => {
    fetchManagers();
    fetchUnassignedAgents();
  }, []);

  const fetchManagers = async () => {
    setLoading(true);
    try {
      const { data: ams, error } = await supabase
        .from("account_managers")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const { data: assignments } = await supabase
        .from("client_am_assignments")
        .select("am_id")
        .eq("status", "active");

      const countMap: Record<string, number> = {};
      (assignments || []).forEach((a) => {
        countMap[a.am_id] = (countMap[a.am_id] || 0) + 1;
      });

      setManagers(
        (ams || []).map((am) => ({
          ...am,
          is_active: am.is_active ?? true,
          max_clients: am.max_clients ?? 50,
          active_client_count: countMap[am.id] || 0,
        }))
      );
    } catch (err: any) {
      console.error("[AdminAM] Error loading managers:", err);
      console.error("[AdminAM] Error details:", JSON.stringify(err, null, 2));
      toast.error(`Failed to load account managers: ${err?.message || err?.code || "unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedAgents = async () => {
    try {
      const { data: agents } = await supabase
        .from("agents")
        .select("id, name, company, vertical, email, assigned_am_id")
        .eq("is_active", true);

      const unassigned = (agents || [])
        .filter((a) => !a.assigned_am_id)
        .map((a) => ({
          id: a.id,
          name: a.name,
          company: a.company,
          vertical: a.vertical,
          email: a.email,
        }));
      setUnassignedAgents(unassigned);
    } catch (err) {
      console.error("[AdminAM] Error fetching unassigned:", err);
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormTitle("Account Manager");
    setFormMaxClients(50);
    setFormSpecialty([]);
    setEditingAM(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (am: AccountManager) => {
    setEditingAM(am);
    setFormName(am.full_name);
    setFormEmail(am.email);
    setFormPhone(am.phone || "");
    setFormTitle(am.title || "Account Manager");
    setFormMaxClients(am.max_clients);
    setFormSpecialty(am.specialty || []);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim() || !formEmail.trim()) {
      toast.error("Name and email are required");
      return;
    }

    try {
      if (editingAM) {
        const { error } = await supabase
          .from("account_managers")
          .update({
            full_name: formName.trim(),
            email: formEmail.trim(),
            phone: formPhone.trim() || null,
            title: formTitle.trim(),
            max_clients: formMaxClients,
            specialty: formSpecialty.length > 0 ? formSpecialty : null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingAM.id);

        if (error) throw error;
        toast.success("Account Manager updated");
      } else {
        // Look up auth.users by email via SECURITY DEFINER RPC
        const { data: rpcResult, error: rpcError } = await supabase.rpc(
          "lookup_user_id_by_email",
          { p_email: formEmail.trim() }
        );

        if (rpcError) {
          console.error("[AdminAM] lookup_user_id_by_email error:", rpcError);
          toast.error("Failed to look up user. Make sure the RPC function exists.");
          return;
        }

        const userId: string | null = rpcResult || null;

        if (!userId) {
          toast.error("No user found with this email. The user must sign up first.");
          return;
        }

        // Check if this user is already an Account Manager
        const { data: existingAM } = await supabase
          .from("account_managers")
          .select("id, full_name")
          .eq("user_id", userId)
          .maybeSingle();

        if (existingAM) {
          toast.error(`This user is already an Account Manager (${existingAM.full_name}).`);
          return;
        }

        const { error: amError } = await supabase
          .from("account_managers")
          .insert({
            user_id: userId,
            full_name: formName.trim(),
            email: formEmail.trim(),
            phone: formPhone.trim() || null,
            title: formTitle.trim(),
            max_clients: formMaxClients,
            specialty: formSpecialty.length > 0 ? formSpecialty : null,
          });

        if (amError) throw amError;

        const { data: existingAdminRow } = await supabase
          .from("admin_users")
          .select("user_id")
          .eq("user_id", userId)
          .maybeSingle();

        if (!existingAdminRow) {
          await supabase.from("admin_users").insert({
            user_id: userId,
            role: "account_manager",
          });
        }

        toast.success("Account Manager added");
      }

      setDialogOpen(false);
      resetForm();
      fetchManagers();
    } catch (err: any) {
      console.error("[AdminAM] Submit error:", err);
      toast.error(err.message || "Failed to save");
    }
  };

  const toggleActive = async (am: AccountManager) => {
    try {
      const { error } = await supabase
        .from("account_managers")
        .update({ is_active: !am.is_active, updated_at: new Date().toISOString() })
        .eq("id", am.id);

      if (error) throw error;
      toast.success(am.is_active ? "Account Manager deactivated" : "Account Manager activated");
      fetchManagers();
    } catch (_err) {
      toast.error("Failed to update status");
    }
  };

  const toggleSpecialty = (s: string) => {
    setFormSpecialty((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  // Quick assign from unassigned section
  const openQuickAssign = (agent: UnassignedAgent) => {
    setAssigningAgentId(agent.id);
    setAssigningAgentName(agent.name);
    setAssignSelectedAmId("");
    setAssignDialogOpen(true);
  };

  const handleQuickAssign = async () => {
    if (!assigningAgentId || !assignSelectedAmId) return;

    try {
      // End existing active assignment if any (same logic as AdminAgents)
      const { data: existing } = await supabase
        .from("client_am_assignments")
        .select("id")
        .eq("agent_id", assigningAgentId)
        .eq("status", "active");

      if (existing && existing.length > 0) {
        await supabase
          .from("client_am_assignments")
          .update({
            status: "transferred",
            ended_at: new Date().toISOString(),
            transfer_reason: "Reassigned by admin",
          })
          .eq("agent_id", assigningAgentId)
          .eq("status", "active");
      }

      // Insert new assignment
      const { error: insertError } = await supabase
        .from("client_am_assignments")
        .insert({
          agent_id: assigningAgentId,
          am_id: assignSelectedAmId,
          status: "active",
          assigned_by: user?.id || null,
        });

      if (insertError) {
        console.error("[AdminAM] Assignment insert failed:", insertError);
        toast.error(`Assignment failed: ${insertError.message}`);
        return;
      }

      // Update denormalized column
      await supabase
        .from("agents")
        .update({ assigned_am_id: assignSelectedAmId })
        .eq("id", assigningAgentId);

      const amName = managers.find((m) => m.id === assignSelectedAmId)?.full_name || "AM";
      toast.success(`${assigningAgentName} assigned to ${amName}`);
      setAssignDialogOpen(false);
      fetchManagers();
      fetchUnassignedAgents();
    } catch (err: any) {
      toast.error(err.message || "Failed to assign");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Account Managers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your internal AM team
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Account Manager
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingAM ? "Edit Account Manager" : "Add Account Manager"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Full Name *</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Sarah Martinez" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="sarah@trustedjames.com" disabled={!!editingAM} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} placeholder="(555) 123-4567" />
              </div>
              <div>
                <Label>Title</Label>
                <Input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
              </div>
              <div>
                <Label>Max Clients</Label>
                <Input type="number" value={formMaxClients} onChange={(e) => setFormMaxClients(parseInt(e.target.value) || 50)} />
              </div>
              <div>
                <Label>Specialty</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SPECIALTIES.map((s) => (
                    <Badge
                      key={s}
                      variant={formSpecialty.includes(s) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleSpecialty(s)}
                    >
                      {formatSpecialty(s)}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button onClick={handleSubmit} className="w-full">
                {editingAM ? "Save Changes" : "Add Account Manager"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div>
      ) : managers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserCog className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No account managers yet. Add your first one above.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Team ({managers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Title</th>
                    <th className="pb-3 font-medium">Clients</th>
                    <th className="pb-3 font-medium">Max</th>
                    <th className="pb-3 font-medium">Specialty</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {managers.map((am) => (
                    <tr key={am.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-3 font-medium text-foreground">{am.full_name}</td>
                      <td className="py-3 text-muted-foreground">{am.email}</td>
                      <td className="py-3 text-muted-foreground">{am.title}</td>
                      <td className="py-3">{am.active_client_count || 0}</td>
                      <td className="py-3 text-muted-foreground">{am.max_clients}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {(am.specialty || []).map((s) => (
                            <Badge key={s} variant="outline" className="text-xs">
                              {formatSpecialty(s)}
                            </Badge>
                          ))}
                          {(!am.specialty || am.specialty.length === 0) && (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant={am.is_active ? "default" : "secondary"}>
                          {am.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(am)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(am)}
                            className={am.is_active ? "text-destructive" : "text-green-600"}
                          >
                            {am.is_active ? "Deactivate" : "Activate"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unassigned Clients Section */}
      {unassignedAgents.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Unassigned Clients ({unassignedAgents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Company</th>
                    <th className="pb-3 font-medium">Vertical</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedAgents.map((agent) => (
                    <tr key={agent.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-3 font-medium text-foreground">{agent.name}</td>
                      <td className="py-3 text-muted-foreground">{agent.company || "—"}</td>
                      <td className="py-3">
                        {formatSpecialty(agent.vertical)}
                      </td>
                      <td className="py-3">
                        <Button variant="outline" size="sm" onClick={() => openQuickAssign(agent)}>
                          Assign AM
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign AM to {assigningAgentName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Account Manager</Label>
              <Select value={assignSelectedAmId} onValueChange={setAssignSelectedAmId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an AM..." />
                </SelectTrigger>
                <SelectContent>
                  {managers.filter((m) => m.is_active).map((am) => (
                    <SelectItem key={am.id} value={am.id}>
                      {am.full_name} ({am.active_client_count || 0}/{am.max_clients})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleQuickAssign} disabled={!assignSelectedAmId} className="w-full">
              Assign
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
